import { Alert, Button, FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import Logger from "../../../services/Logger";
import Box from "@mui/material/Box";
import React, { useState } from "react";
import TranslateService from "./TranslateService";

const languages = [
    { code: "en", name: "English" },
    { code: "fr", name: "French" },
    { code: "es", name: "Spanish" },    
    { code: "de", name: "German" },
    { code: "it", name: "Italian" },
    { code: "pt", name: "Portuguese" },
    { code: "ru", name: "Russian" },
    { code: "ja", name: "Japanese" },
    { code: "ko", name: "Korean" },
    { code: "zh", name: "Chinese" },
];

export default function TranslateMain() {
    const [text, setText] = useState("");
    const [sourceLanguage, setSourceLanguage] = useState("en");
    const [targetLanguage, setTargetLanguage] = useState("fr");
    const [translatedText, setTranslatedText] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleTranslate = async () => {
        if (!text || !text.trim()) {
            setError("Please enter text to translate");
            return;
        }

        if (sourceLanguage === targetLanguage) {
            setError("Source and target languages must be different");
            return;
        }

        setLoading(true);
        setError(null);
        setTranslatedText("");

        try {
            const result = await TranslateService.translate(
                text.trim(),
                sourceLanguage,
                targetLanguage
            );
            
            if (result && result.translatedText) {
                setTranslatedText(result.translatedText);
            } else {
                setError("Translation not available");
            }
        } catch (err) {
            Logger.error("Translation error:", err);
            setError(err.message || "Failed to translate. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleTranslate();
        }
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}>
            <Box sx={{ display: "flex", gap: 2 }}>
                <FormControl fullWidth>
                    <InputLabel id="source-language-label">Source Language</InputLabel>
                    <Select
                        labelId="source-language-label"
                        value={sourceLanguage}
                        label="Source Language"
                        onChange={(e) => setSourceLanguage(e.target.value)}
                    >
                        {languages.map((lang) => (
                            <MenuItem key={lang.code} value={lang.code}>
                                {lang.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl fullWidth>
                    <InputLabel id="target-language-label">Target Language</InputLabel>
                    <Select
                        labelId="target-language-label"
                        value={targetLanguage}
                        label="Target Language"
                        onChange={(e) => setTargetLanguage(e.target.value)}
                    >
                        {languages.map((lang) => (
                            <MenuItem key={lang.code} value={lang.code}>
                                {lang.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>
            <TextField
                label="Text to Translate"
                variant="outlined"
                value={text}
                onChange={(e) => {
                    setText(e.target.value);
                    setError(null);
                }}
                onKeyPress={handleKeyPress}
                fullWidth
                placeholder="Enter text to translate"
                multiline
                rows={4}
            />
            <Box>
                <Button 
                    variant="contained" 
                    onClick={handleTranslate}
                    disabled={loading || !text.trim() || sourceLanguage === targetLanguage}
                >
                    {loading ? "Translating..." : "Translate"}
                </Button>
            </Box>
            {error && (
                <Alert severity="error">
                    {error}
                </Alert>
            )}
            {translatedText && (
                <TextField
                    label="Translation"
                    variant="outlined"
                    value={translatedText}
                    fullWidth
                    disabled
                    multiline
                    rows={4}
                />
            )}
        </Box>
    );
}

